import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../services/api';
import AuthLayout from '../components/Layout/AuthLayout';

/**
 * StudentRegisterPage - Two-step registration form for students (COPPA compliant)
 * Step 1: Validate activation code and load pre-filled data
 * Step 2: Complete registration with username and password
 */
const StudentRegisterPage = () => {
  const [step, setStep] = useState(1); // 1 = Enter Code, 2 = Complete Registration
  const [activationCode, setActivationCode] = useState('');
  const [studentData, setStudentData] = useState(null);
  const [teacherData, setTeacherData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { registerStudent } = useAuth();

  // Step 1: Validate activation code
  const handleValidateCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!activationCode.trim()) {
      setError('Please enter your activation code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiRequest('/api/auth/student/validate-code', 'POST', {
        activation_code: activationCode
      });

      setStudentData(response.student_data);
      setTeacherData(response.teacher_data);
      setSchoolData(response.school_data);

      // Pre-fill form data if names are provided
      if (response.student_data.has_prefilled_name) {
        setFormData({
          ...formData,
          firstName: response.student_data.first_name || '',
          lastName: response.student_data.last_name || ''
        });
      }

      setStep(2);
    } catch (err) {
      setError(err.message || 'Invalid activation code. Please check and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Complete registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.username || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await registerStudent(
        formData.username,
        formData.password,
        formData.firstName || studentData.first_name || '',
        formData.lastName || studentData.last_name || '',
        activationCode
      );

      setSuccess('Account created successfully! Redirecting to login...');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login/student');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <AuthLayout showBackButton={step === 1}>
      <form className="w-full" onSubmit={step === 1 ? handleValidateCode : handleSubmit}>
        {step === 2 && (
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setStudentData(null);
              setTeacherData(null);
              setSchoolData(null);
              setError('');
            }}
            className="mb-6 text-violet-600 hover:text-violet-700 text-sm flex items-center font-semibold"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Change activation code
          </button>
        )}

        {/* Icon & Title */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 blur-3xl opacity-40 bg-violet-500" />
            <div className="relative bg-gradient-to-br from-violet-500 to-purple-600 rounded-[1.5rem] p-6 shadow-2xl">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            Student Registration
          </h2>
          <p className="text-gray-500 text-base">
            {step === 1 ? 'Enter your activation code to begin' : `Join ${teacherData?.full_name || "your teacher"}'s class`}
          </p>
        </div>

        {/* Step 1: Enter and Validate Activation Code */}
        {step === 1 && (
          <>
            <div className="form-group mb-6 p-5 bg-violet-50 rounded-2xl border-2 border-violet-200">
              <label htmlFor="student-code" className="block mb-3 font-bold text-violet-900 text-sm">
                Student Activation Code*
              </label>
              <input
                type="text"
                id="student-code"
                name="studentCode"
                placeholder="STU-XXXXXXXX"
                className="w-full py-3 px-4 border-2 border-violet-300 rounded-xl bg-white focus:outline-none focus:border-violet-500 font-mono text-lg transition-colors"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                required
                disabled={isLoading}
                autoFocus
              />
              <p className="text-xs text-violet-700 mt-3 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Ask your teacher for this code
              </p>
            </div>

            <button
              type="submit"
              className={`w-full bg-gradient-to-r from-violet-600 to-purple-700 text-white font-semibold py-4 px-6 rounded-2xl
                         hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide mb-4 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              {isLoading ? 'Validating Code...' : 'Continue →'}
            </button>
          </>
        )}

        {/* Step 2: Complete Registration with Pre-filled Data */}
        {step === 2 && studentData && (
          <>
            {/* School/Teacher Information Banner */}
            <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-2xl">
              <p className="text-sm text-green-900 font-bold flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Code Verified
              </p>
              <p className="text-xs text-green-700 mt-2 ml-7">
                {schoolData?.school_name}
                {teacherData?.full_name && ` • ${teacherData.full_name}'s Class`}
                {teacherData?.subject && ` • ${teacherData.subject}`}
              </p>
            </div>

            {/* Pre-filled Name Fields (if teacher added names) */}
            {studentData.has_prefilled_name && (
              <div className="mb-6 p-4 bg-gray-50 rounded-2xl border-2 border-gray-200">
                <p className="text-xs text-gray-600 mb-3 font-bold">Pre-filled Information (from your teacher)</p>

                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group">
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={studentData.first_name}
                      className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={studentData.last_name}
                      className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Editable Name Fields (if quick-generated) */}
            {!studentData.has_prefilled_name && (
              <div className="mb-6">
                <p className="text-xs text-gray-600 mb-3 font-bold">Your Information (Optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group">
                    <label htmlFor="first-name" className="block mb-2 text-sm font-semibold text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="first-name"
                      name="firstName"
                      placeholder="John"
                      className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-violet-500 transition-colors"
                      value={formData.firstName}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="last-name" className="block mb-2 text-sm font-semibold text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="last-name"
                      name="lastName"
                      placeholder="Smith"
                      className="w-full py-2 px-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-violet-500 transition-colors"
                      value={formData.lastName}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Grade Level (Always Read-only from Teacher) */}
            <div className="mb-6 p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
              <p className="text-xs text-blue-700 mb-3 font-bold">
                Your grade level (set by your teacher)
              </p>
              <div className="flex items-center gap-3">
                <div className="text-3xl">🎓</div>
                <span className="text-lg font-bold text-blue-900 capitalize">
                  {studentData.grade_level === 'elementary' ? 'Elementary (K-5)' : 'Middle School (6-8)'}
                </span>
              </div>
            </div>

            {/* Required Registration Fields */}
            <p className="text-xs text-gray-600 mb-4 font-bold">Create Your Login</p>

            <div className="space-y-4 mb-6">
              <div className="form-group">
                <label htmlFor="username" className="block mb-2 font-semibold text-gray-800 text-sm">
                  Username*
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  placeholder="cool_student_123"
                  className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-violet-500 transition-colors"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                />
                <p className="text-xs text-gray-500 mt-2">
                  This is what you'll use to log in
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="block mb-2 font-semibold text-gray-800 text-sm">
                  Password*
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Create a password (min 8 characters)"
                  className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-violet-500 transition-colors"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password" className="block mb-2 font-semibold text-gray-800 text-sm">
                  Confirm Password*
                </label>
                <input
                  type="password"
                  id="confirm-password"
                  name="confirmPassword"
                  placeholder="Re-enter password"
                  className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-violet-500 transition-colors"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <button
              type="submit"
              className={`w-full bg-gradient-to-r from-violet-600 to-purple-700 text-white font-semibold py-4 px-6 rounded-2xl
                         hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide mb-4 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Student Account'}
            </button>

            <div className="mt-4 p-3 bg-blue-50 border-2 border-blue-200 rounded-2xl text-xs text-blue-800 flex items-start gap-2">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <strong>Privacy Note:</strong> We don't collect email addresses from students to comply with COPPA regulations.
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm text-center">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border-2 border-green-200 rounded-xl text-green-700 text-sm text-center font-semibold">
            {success}
          </div>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-400">Already have an account?</span>
          </div>
        </div>

        <Link
          to="/login/student"
          className="block text-center w-full bg-gray-100 text-gray-800 font-semibold py-4 px-6 rounded-2xl
                   hover:bg-gray-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 text-base"
        >
          Sign In
        </Link>
      </form>
    </AuthLayout>
  );
};

export default StudentRegisterPage;
